import { Navigate } from 'react-router-dom'
import { usePrivy } from '@privy-io/react-auth'
import type { ReactNode } from 'react'
import { useStore } from '../store'
import { Icon } from './ui/Icon'

interface ProtectedRouteProps {
  children: ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { ready, authenticated } = usePrivy()
  const { app } = useStore()
  const { venue, pendingVenueId } = app

  // Show loading while Privy initializes OR while we are validating a pending venue
  // This prevents flashing the "Invalid Access" screen during the AuthSync process
  if (!ready || pendingVenueId) {
    return (
      <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--color-secondary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--color-text-muted)] text-sm">
            {pendingVenueId ? 'Verifying Venue...' : 'Loading...'}
          </p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!authenticated) {
    return <Navigate to="/" replace />
  }

  // Global Venue Guard: Block access if authenticated but no venue session
  if (!venue?.id) {
    // ACTIVE ERROR STATE: User tried to scan a bad code
    if (app.error) {
       return (
        <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col items-center justify-center px-6">
          <div className="w-full max-w-sm text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 mb-6">
              <Icon name="alert-triangle" size={32} className="text-[var(--color-error)]" />
            </div>
            <h1 className="text-2xl font-semibold text-[var(--color-text)] mb-3">
              Invalid Link
            </h1>
            <p className="text-[var(--color-text-muted)] text-base mb-8 px-4">
              This QR code is invalid or expired. Please try scanning another code.
            </p>
          </div>
        </div>
      )
    }

    // PASSIVE STATE: User hasn't scanned anything yet
    return (
      <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--color-surface)] mb-6">
            <Icon name="camera" size={32} className="text-[var(--color-primary)]" />
          </div>
          <h1 className="text-2xl font-semibold text-[var(--color-text)] mb-3">
            Ready to Pay?
          </h1>
          <p className="text-[var(--color-text-muted)] text-base mb-8 px-4">
            To start your tab, please scan the QR code located at your table or venue.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
